import { SIDES } from './geometry'

// ─── Hex board — SVG defs ─────────────────────────────────────────────────────
// Every gradient and filter the layers reference by id. Each is annotated with
// the rule it mirrors in the retired square board's BoardGrid.css / BattlefieldCell.css.
export default function HexDefs({ landClipCells }) {
    return (
        <defs>
            {/* .bfc --plot-noise: fractal grain at ~9% black */}
            <filter id="hexb-noise" x="0" y="0" width="100%" height="100%" filterUnits="userSpaceOnUse">
                <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="2" stitchTiles="stitch" />
                <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.09 0" />
            </filter>
            <clipPath id="hexb-land-clip">
                {SIDES.flatMap(side => landClipCells[side].map(c => <polygon key={`${side}-${c.s}`} points={c.pts} />))}
            </clipPath>
            {/* .bg__ps-cell--damaged: charred land. Flatter than the square
                board's rim shadow — a near-even char with only a gentle
                darkening at the edge, so a field of craters reads as one
                burnt zone rather than a grid of rings. */}
            <radialGradient id="hexb-crater">
                <stop offset="0%"   stopColor="rgba(0, 0, 0, 0.24)" />
                <stop offset="60%"  stopColor="rgba(0, 0, 0, 0.26)" />
                <stop offset="100%" stopColor="rgba(0, 0, 0, 0.37)" />
            </radialGradient>
            {/* .bg__ps-stroke--hit: inset 0 0 10px 3px rgba(255,136,0,0.6) */}
            <radialGradient id="hexb-glow-hit">
                <stop offset="0%"   stopColor="rgba(255, 136, 0, 0)" />
                <stop offset="35%"  stopColor="rgba(255, 136, 0, 0.1)" />
                <stop offset="100%" stopColor="rgba(255, 136, 0, 0.6)" />
            </radialGradient>
            {/* .bg__ps-stroke--scanned: the same glow in structure yellow */}
            <radialGradient id="hexb-glow-scanned">
                <stop offset="0%"   stopColor="rgba(240, 224, 64, 0)" />
                <stop offset="35%"  stopColor="rgba(240, 224, 64, 0.1)" />
                <stop offset="100%" stopColor="rgba(240, 224, 64, 0.65)" />
            </radialGradient>
            {/* .bg__ps-stroke--dead: inset 0 0 8px 3px rgba(0,0,0,0.65) */}
            <radialGradient id="hexb-glow-dead">
                <stop offset="0%"   stopColor="rgba(0, 0, 0, 0)" />
                <stop offset="40%"  stopColor="rgba(0, 0, 0, 0.09)" />
                <stop offset="100%" stopColor="rgba(0, 0, 0, 0.39)" />
            </radialGradient>
            {/* .bg__ps-cell--scan-self: inset 0 0 5px 1px rgba(168,85,247,0.22) */}
            <radialGradient id="hexb-glow-scan-self">
                <stop offset="0%"   stopColor="rgba(168, 85, 247, 0)" />
                <stop offset="60%"  stopColor="rgba(168, 85, 247, 0)" />
                <stop offset="100%" stopColor="rgba(168, 85, 247, 0.22)" />
            </radialGradient>
            {/* Caret shadow softness (the offset is applied to the shadow copy itself). */}
            <filter id="hexb-caret-shadow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="1.4" />
            </filter>
            {/* Drone ground shadow softness. */}
            <filter id="hexb-shadow-blur" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.4" />
            </filter>
            {/* Airspace feathering: a touch of blur on the haze fill only, so
                the atmosphere stays soft inside the crisp stroked edge. */}
            <filter id="hexb-lane-feather" x="-25%" y="-10%" width="150%" height="120%">
                <feGaussianBlur stdDeviation="0.8" />
            </filter>
            {/* Haze runs thin at the band end and thickens toward the far end
                (self: bottom, opp: top) so the pill's cap carries the capacity
                boundary. Cool blue-white: air over warm ground. */}
            {SIDES.map(side => {
                const far = side === 'self' ? { y1: 0, y2: 1 } : { y1: 1, y2: 0 }
                return (
                    <linearGradient key={side} id={`hexb-lane-haze-${side}`} x1="0" x2="0" {...far}>
                        <stop offset="0%"   stopColor="rgba(200, 222, 255, 0.06)" />
                        <stop offset="55%"  stopColor="rgba(200, 222, 255, 0.09)" />
                        <stop offset="100%" stopColor="rgba(200, 222, 255, 0.20)" />
                    </linearGradient>
                )
            })}
            {/* An armed self lane: the airspace in attack red (COLOR.BTN_ATTACK
                #e05050), same thickening toward the far end. */}
            <linearGradient id="hexb-lane-haze-armed" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"   stopColor="rgba(224, 80, 80, 0.16)" />
                <stop offset="55%"  stopColor="rgba(224, 80, 80, 0.20)" />
                <stop offset="100%" stopColor="rgba(224, 80, 80, 0.32)" />
            </linearGradient>
            {/* .bg__ps-cell--fresh::after — the fiery ignition flash as the fire
                front passes a fresh crater; .bg__ps-cell--scan-fresh::after — the
                purple sensor flash as a scan sweeps in. */}
            <radialGradient id="hexb-flash-fire">
                <stop offset="0%"   stopColor="rgba(255, 236, 170, 0.95)" />
                <stop offset="45%"  stopColor="rgba(255, 150, 40, 0.75)" />
                <stop offset="75%"  stopColor="rgba(255, 80, 0, 0.35)" />
                <stop offset="100%" stopColor="rgba(255, 80, 0, 0)" />
            </radialGradient>
            <radialGradient id="hexb-flash-scan">
                <stop offset="0%"   stopColor="rgba(240, 225, 255, 0.95)" />
                <stop offset="45%"  stopColor="rgba(168, 85, 247, 0.65)" />
                <stop offset="75%"  stopColor="rgba(168, 85, 247, 0.25)" />
                <stop offset="100%" stopColor="rgba(168, 85, 247, 0)" />
            </radialGradient>
            {/* .bg__ps-distance text-shadow: 0 0 2px + 0 0 1px black */}
            <filter id="hexb-stamp-shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="0.8" floodColor="#000" floodOpacity="0.95" />
            </filter>
        </defs>
    )
}
